import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { Tenant, fetchTenantList, fetchTenantVpaMappings, TenantListResponse, fetchProgramVpaMappings, deleteTenant } from "../../features/outlet/TenantSlice";
import { SearchX, Trash2, X } from "lucide-react";
import { fetchProgramById, fetchProgramList, Program } from "../../features/program/ProgramSlice";
import { fetchPrincipalList, Principal } from "../../features/principal/PrincipalSlice";
import { Search } from "lucide-react";
import ExcelJS from 'exceljs';
import { Building2, Calendar, User, CreditCard, Pencil, Settings } from "lucide-react";

interface TenantListProps {
  programId: number | string | null;
  principalId: number | string | null;
  setProgramId: (programId: string | number | null) => void;
  setPrincipalId: (programId: string | number | null) => void;
  tenantList: TenantListResponse;
  handleEditClick: (tenant: Tenant) => void;
  setSelectedTenant: (tenant: Tenant) => void;
  showUploadModal: boolean;
  setShowUploadModal: (showUploadModal: boolean) => void;
  programList: Program[] | null;
  principalList: Principal[] | null;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  searchTerm: string

}

// Confirmation Dialog Component (similar to Programs list)
const ConfirmationDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
}> = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const TenantList: React.FC<TenantListProps> = ({ handleEditClick, setSelectedTenant, tenantList, principalList, programList, setShowUploadModal, programId, principalId, setPrincipalId, setProgramId, currentPage, setCurrentPage, searchTerm, setSearchTerm }) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const selectedProgram = useAppSelector((state: any) => state.program.selectedProgram);
  const tenantVpaMappings = useAppSelector((state: any) => state.tenant.tenantVpaMappings);
  const programVpaMappings = useAppSelector((state: any) => state.tenant.programVpaMappings);

  /* --------------------------------- States ------------------------------ */

  const [totalPages, setTotalPages] = useState(1);
  const [deletingTenantId, setDeletingTenantId] = useState<number | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    tenant: Tenant | null;
  }>({
    isOpen: false,
    tenant: null,
  });

  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    if (tenantList) {
      setTotalPages(tenantList.total_pages);
    }
  }, [dispatch, tenantList]);

  /* ------------------------------ Event Handlers ---------------------------- */

  const handleSearch = () => {
    dispatch(fetchTenantList({ programId, principalId, page: 1, searchTerm }));
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    const vpaCount = tenant.vpaCount ?? 0;
    if (vpaCount > 0) {
      return;
    }

    setConfirmationDialog({
      isOpen: true,
      tenant,
    });
  };

  const confirmDeleteTenant = async () => {
    if (!confirmationDialog.tenant) return;

    try {
      setDeletingTenantId(confirmationDialog.tenant.tenantId);
      const resultAction = await dispatch(deleteTenant(confirmationDialog.tenant.tenantId));

      if (deleteTenant.rejected.match(resultAction)) {
        const errorMessage = (resultAction.payload as string) || "Failed to delete outlet";
        window.alert(errorMessage);
      }
    } finally {
      setDeletingTenantId(null);
    }
  };
  const downloadProgramVpas = async () => {
    if (!programId) return;
    
    const response = await dispatch(fetchProgramVpaMappings({ programId: Number(programId) }));
    const programResponse = await dispatch(fetchProgramById(Number(programId)));
    
    if (response.type.endsWith('/fulfilled') && programResponse.type.endsWith('/fulfilled')) {
      const programName = programList?.find(p => p.programId === programId)?.programName || 'program';
      const merchantId = programResponse.payload?.merchantId;
      
      const data = response.payload.flatMap((mapping: any) => 
        mapping.vpa.map((vpa: any) => ({
          MERCHANT_ID: merchantId,
          VPA: vpa.vpa,
          "STATUS (Enable/Disable)": vpa.status === 'ACTIVE' ? 'ENABLE' : 'DISABLE'
        }))
      ) || [];

      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      // Define columns
      worksheet.columns = [
        { header: 'MERCHANT_ID', key: 'MERCHANT_ID', width: 20 },
        { header: 'VPA', key: 'VPA', width: 30 },
        { header: 'STATUS (Enable/Disable)', key: 'STATUS (Enable/Disable)', width: 20 }
      ];

      // Add data rows
      worksheet.addRows(data);

      // Style header row
      const headerRow = worksheet.getRow(1);

      // Apply styles to each header cell
      headerRow.eachCell((cell) => {
        cell.font = {
          name: 'Calibri',
          size: 10,
          bold: true,
        };
        
      });

      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${programName}_vpas.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  /* ------------------------------ UI Handlers ---------------------------- */

  const getPagination = (
    <div className="flex justify-center my-8 bg-white">
      <div className="inline-flex rounded-md shadow-sm" role="group">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 focus:z-10 focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-t border-b border-gray-200 hover:bg-gray-100 hover:text-gray-700 focus:z-10 focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-brand border-t border-b border-brand hover:bg-brand/90 focus:z-10 focus:ring-2 focus:ring-brand/50"
        >
          Page {Math.min(currentPage, totalPages)} of {totalPages}
        </button>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-t border-b border-gray-200 hover:bg-gray-100 hover:text-gray-700 focus:z-10 focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 focus:z-10 focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Last
        </button>
      </div>
    </div>
  );

  const getContent = () => {
    if (tenantList.total_count !== 0) {
      return (
        <div className="space-y-6">
          {tenantList.tenants.map((tenant, index) => (
            <div key={index} className="bg-white rounded-lg border border-cardborder  hover:shadow-sm transition-shadow">
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      {tenant.storeLogo ? (
                        <img
                          src={tenant.storeLogo}
                          alt={tenant.tenantBrand}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building2 className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <h6 className="text-sm font-medium text-gray-900 mb-1">{tenant.tenantBrand}</h6>
                      <p className="text-sm text-gray-500 line-clamp-1">
                        {[
                          tenant.address,
                          tenant.city,
                          tenant.state,
                          tenant.pincode
                        ].filter(Boolean).join(", ")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center  border-r border-gray-200 pr-6">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-500">Added By</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700 mt-1">Admin</span>
                    </div>

                    <div className="flex flex-col items-center  border-r border-gray-200 pr-6">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-500">Added On</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700 mt-1">{new Date(tenant.createdAt).toLocaleDateString() || ""}</span>
                    </div>

                    <div className="flex flex-col items-center ">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <CreditCard className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-500">VPAs</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700 mt-1">{tenant.vpaCount || 0}</span>
                    </div>

                    <div className="flex items-center gap-3 ml-2">
                      <button
                        disabled={!tenant.vpaCount}
                        onClick={async () => {
                          await dispatch(fetchTenantVpaMappings(tenant.tenantId));
                          setSelectedTenant(tenant);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Settings className="w-4 h-4" />
                        Manage VPAs
                      </button>
                      <button
                        onClick={() => handleEditClick(tenant)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(tenant)}
                        disabled={!!tenant.vpaCount || deletingTenantId === tenant.tenantId}
                        className="inline-flex items-center justify-center rounded-md p-2 bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Delete outlet"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3 text-gray-500">
            <SearchX className="w-12 h-12" />
            <p className="text-lg font-medium">No Outlets Found</p>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className="bg-white px-8">
        {getContent()}
      </div>
      {getPagination}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() =>
          setConfirmationDialog({
            isOpen: false,
            tenant: null,
          })
        }
        onConfirm={confirmDeleteTenant}
        title="Delete Outlet"
        message={
          confirmationDialog.tenant
            ? `Are you sure you want to delete the outlet "${confirmationDialog.tenant.tenantBrand}"?`
            : ""
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default TenantList;
